'use client'

import { m } from 'framer-motion'
import { Sparkles } from 'lucide-react'

interface MaykopSEOVariantsProps {
  title?: string
  text?: string[]
  kicker?: string
  signature?: string
}

export default function MaykopSEOVariants({
  title,
  text,
  kicker = 'SEO & Аналитика',
  signature = 'Цифровой горизонт / Adygea',
}: MaykopSEOVariantsProps) {
  if (!title || !text) return null

  return (
    <section className="relative overflow-hidden py-12 sm:py-16 md:py-20">
      <div className="relative z-10 mx-auto max-w-[1800px] px-4 sm:px-6 lg:px-8">
        {/* Main Glass Card */}
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-[24px] border border-white/10 p-5 sm:rounded-[40px] sm:p-8 md:p-16"
        >
          {/* --- Premium Background Layers --- */}

          {/* 1. Base Dark Glass */}
          <div className="absolute inset-0 bg-[#0a0f1a]/20 backdrop-blur-xl" />

          {/* 2. Aurora Gradients (Animated) - Reduced opacity */}
          <m.div
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
            className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(56,189,248,0.1),_transparent_50%),_radial-gradient(circle_at_0%_0%,_rgba(168,85,247,0.1),_transparent_50%)]"
          />

          {/* 4. Subtle Shimmer Border (Inner) */}
          <div className="pointer-events-none absolute inset-[1px] rounded-[39px] border border-white/5" />

          {/* --- Content --- */}
          <div className="relative z-10">
            {/* Header Area */}
            <div className="mb-8 max-w-4xl sm:mb-12 md:mb-16">
              <div className="mb-4 flex items-center gap-3 sm:mb-6">
                <div className="h-px w-8 bg-blue-500/50 sm:w-12" />
                <span className="text-xs font-bold tracking-[0.2em] text-blue-400 uppercase sm:text-sm">
                  {kicker}
                </span>
              </div>
              <h2 className="text-2xl leading-[1.1] font-bold tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
                <span className="bg-gradient-to-r from-white via-white to-white/50 bg-clip-text text-transparent">
                  {title}
                </span>
              </h2>
            </div>

            {/* Text Grid */}
            <div className="grid grid-cols-1 gap-6 sm:gap-10 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
              <div className="space-y-5 sm:space-y-8">
                <p className="text-base leading-relaxed font-light text-gray-300 sm:text-lg md:text-xl">
                  {text[0]}
                </p>
                <p className="text-base leading-relaxed font-light text-gray-400 sm:text-lg md:text-xl">
                  {text[1]}
                </p>
              </div>
              <div className="relative space-y-5 sm:space-y-8">
                {/* Decorative vertical line on desktop */}
                <div className="absolute top-2 bottom-2 -left-10 hidden w-px bg-gradient-to-b from-transparent via-white/10 to-transparent md:block" />

                <p className="text-base leading-relaxed font-light text-gray-400 sm:text-lg md:text-xl">
                  {text[2]}
                </p>
                <div className="rounded-xl border border-white/5 bg-white/5 p-4 backdrop-blur-sm sm:rounded-2xl sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-blue-500/10 p-2 text-blue-400">
                      <Sparkles size={20} />
                    </div>
                    <p className="text-base leading-relaxed text-gray-300">{text[3]}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer / Decorative Elements */}
            <div className="mt-10 flex items-center justify-between border-t border-white/5 pt-6 opacity-50 sm:mt-16 sm:pt-8">
              <div className="flex gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <div className="h-2 w-2 rounded-full bg-purple-500" />
                <div className="h-2 w-2 rounded-full bg-cyan-500" />
              </div>
              <span className="text-xs tracking-widest text-white uppercase">{signature}</span>
            </div>
          </div>
        </m.div>
      </div>
    </section>
  )
}
